import { useCallback, useEffect, useMemo, useState } from "react";
import { Wallet } from "lucide-react";
import Panel from "../../components/Panel";
import StatCard from "../../components/StatCard";
import { BtnDanger, BtnSecondary, EmptyState, FormModal, PageHeader, Pagination } from "../../components/erp";
import { financeAPI } from "../../services/businessErpService";
import { financeStatusClass, formatCents, formatDate, titleCase } from "./financeUtils";
import ShopSelect from "../../../components/ShopSelect";
import { useBusinessAuth } from "../../context/BusinessAuthContext";

const PAYABLES_PAGE_SIZE = 6;

function centsToPesoString(value) {
  return value == null ? "" : (Number(value) / 100).toFixed(2);
}

export default function FinancePayables() {
  const auth = useBusinessAuth();
  // Recording a payment is its own permission, separate from approving (FIN-1).
  const canRecordPayments = String(auth?.role || "").toUpperCase() === "OWNER" || Boolean(auth?.hasPermission?.("finance:payments:record"));
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ q: "", status: "all", kind: "all" });
  const [target, setTarget] = useState(null);
  const [form, setForm] = useState({ amount: "", reference_no: "", remarks: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await financeAPI.listPayables(filters);
      setRows(Array.isArray(res?.items) ? res.items : []);
      setSummary(res?.summary || {});
    } catch {
      setRows([]);
      setSummary({});
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => { setPage(1); }, [filters]);

  const totalPages = Math.max(1, Math.ceil(rows.length / PAYABLES_PAGE_SIZE));
  const visibleRows = rows.slice((page - 1) * PAYABLES_PAGE_SIZE, page * PAYABLES_PAGE_SIZE);

  useEffect(() => {
    setPage((current) => Math.min(current, totalPages));
  }, [totalPages]);

  const kindOptions = useMemo(() => [
    { value: "all", label: "All Payables" },
    { value: "supplier", label: "Supplier" },
    { value: "employee", label: "Employee" },
    { value: "payroll", label: "Payroll" },
  ], []);

  const openSettle = (row) => {
    setTarget(row);
    setForm({
      amount: centsToPesoString(row.amountRemainingCents),
      reference_no: "",
      remarks: "",
    });
  };

  const submit = async () => {
    if (!target || saving) return;
    const amountCents = Math.round(Number(form.amount || 0) * 100);
    if (!Number.isFinite(amountCents) || amountCents < 1) {
      alert("Enter a valid payment amount.");
      return;
    }
    if (amountCents > Number(target.amountRemainingCents || 0)) {
      alert("The payment cannot be more than what is still owed.");
      return;
    }
    setSaving(true);
    try {
      await financeAPI.releaseDisbursement({
        targetType: target.sourceSystem,
        targetId: target.id,
        amount_cents: amountCents,
        reference_no: form.reference_no || null,
        remarks: form.remarks || null,
      });
      setTarget(null);
      await load();
    } catch (err) {
      alert(err?.serverMessage || err?.message || "The payment was not recorded.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Finance"
        title="Finance Payables"
        subtitle="Track supplier obligations and employee-linked financial obligations in one operational queue."
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard title="Open Balance" value={loading ? "—" : formatCents(summary.open_amount_cents)} icon={Wallet} />
        <StatCard title="Overdue" value={loading ? "—" : formatCents(summary.overdue_amount_cents)} icon={Wallet} accent />
        <StatCard title="Settled" value={loading ? "—" : formatCents(summary.settled_amount_cents)} icon={Wallet} />
      </div>

      <Panel title="Payables Queue" subtitle="Source-linked obligations ready for settlement">
        <div className="mb-4 grid gap-3 md:grid-cols-[1.2fr_0.6fr_0.6fr]">
          <input
            value={filters.q}
            onChange={(e) => setFilters((current) => ({ ...current, q: e.target.value }))}
            placeholder="Search title, counterparty, or reference…"
            className="rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm outline-none dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-100"
          />
          <ShopSelect
            value={filters.status}
            onValueChange={(value) => setFilters((current) => ({ ...current, status: value }))}
            options={["all", "open", "partially_settled", "settled", "overdue", "voided"].map((value) => ({ value, label: titleCase(value) }))}
            ariaLabel="Filter payables by status"
            align="start"
            minPanelWidth={176}
            triggerClassName="min-h-10 w-full rounded-xl px-3 py-2 text-sm"
            panelClassName="py-1"
          />
          <ShopSelect
            value={filters.kind}
            onValueChange={(value) => setFilters((current) => ({ ...current, kind: value }))}
            options={kindOptions}
            ariaLabel="Filter payables by type"
            align="start"
            minPanelWidth={176}
            triggerClassName="min-h-10 w-full rounded-xl px-3 py-2 text-sm"
            panelClassName="py-1"
          />
        </div>

        {loading ? (
          <div className="text-sm text-neutral-500 dark:text-neutral-400">Loading payables…</div>
        ) : !rows.length ? (
          <EmptyState icon={Wallet} title="No payables found" description="No obligations match the current filters." />
        ) : (
          <div className="overflow-hidden rounded-2xl border border-neutral-200 dark:border-neutral-800 lg:flex lg:min-h-[calc(100dvh-28rem)] lg:flex-col">
            <div className="overflow-x-auto lg:flex lg:flex-1 lg:flex-col">
            <table className="w-full min-w-[920px] text-left text-sm bg-white dark:bg-neutral-950 lg:h-full">
              <thead className="bg-neutral-50 dark:bg-neutral-900/60">
                <tr className="border-b border-neutral-200 dark:border-neutral-800">
                  <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Record</th>
                  <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Counterparty</th>
                  <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Due</th>
                  <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400 text-right">Remaining</th>
                  <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Status</th>
                  <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200 dark:divide-neutral-800">
                {visibleRows.map((row) => (
                  <tr key={row.rowKey}>
                    <td className="px-3 py-2">
                      <div className="font-medium text-neutral-900 dark:text-neutral-100">{row.title}</div>
                      <div className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">{titleCase(row.typeLabel)} • {row.referenceLabel}</div>
                    </td>
                    <td className="px-3 py-2 text-neutral-700 dark:text-neutral-300">
                      <div>{row.counterpartyName || "—"}</div>
                      {row.description ? <div className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">{row.description}</div> : null}
                    </td>
                    <td className="px-3 py-2 text-neutral-700 dark:text-neutral-300">{formatDate(row.dueDate || row.createdAt)}</td>
                    <td className="px-3 py-2 text-right font-medium text-neutral-900 dark:text-neutral-100">{formatCents(row.amountRemainingCents)}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${financeStatusClass(row.statusDisplay)}`}>{titleCase(row.statusDisplay)}</span>
                    </td>
                    <td className="px-3 py-2">
                      {row.manualActionAllowed && canRecordPayments ? (
                        <button
                          onClick={() => openSettle(row)}
                          className="rounded-lg border border-neutral-200 px-3 py-1.5 text-xs font-medium text-neutral-700 hover:bg-neutral-50 dark:border-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-900"
                        >
                          Record payment
                        </button>
                      ) : (
                        <span className="text-xs text-neutral-500 dark:text-neutral-400">
                          {row.manualActionBlockedReason
                            || (row.manualActionAllowed ? "Needs permission to record payments" : "No action")}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            <div className="flex min-h-[4.25rem] flex-col justify-center gap-2 border-t border-neutral-200 bg-neutral-50 px-4 sm:flex-row sm:items-center sm:justify-between dark:border-neutral-800 dark:bg-neutral-900/60 lg:shrink-0">
              <span className="text-xs text-neutral-500 dark:text-neutral-400">Showing {(page - 1) * PAYABLES_PAGE_SIZE + 1}-{Math.min(page * PAYABLES_PAGE_SIZE, rows.length)} of {rows.length}</span>
              <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
            </div>
          </div>
        )}
      </Panel>

      <FormModal
        open={!!target}
        onClose={() => setTarget(null)}
        title="Record Payment"
        size="sm"
        footer={
          <>
            <BtnSecondary onClick={() => setTarget(null)}>Cancel</BtnSecondary>
            <BtnDanger onClick={submit} disabled={saving}>{saving ? "Recording…" : "Record payment"}</BtnDanger>
          </>
        }
      >
        <div className="space-y-3">
          <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-3 text-sm dark:border-neutral-800 dark:bg-neutral-900/50">
            <div className="font-medium text-neutral-900 dark:text-neutral-100">{target?.title}</div>
            <div className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">{target?.counterpartyName || "—"} • Remaining {formatCents(target?.amountRemainingCents)}</div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Amount</label>
            <input type="number" min="0.01" step="0.01" value={form.amount} onChange={(e) => setForm((current) => ({ ...current, amount: e.target.value }))} className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm outline-none dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-100" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Reference No.</label>
            <input value={form.reference_no} onChange={(e) => setForm((current) => ({ ...current, reference_no: e.target.value }))} className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm outline-none dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-100" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Remarks</label>
            <textarea rows={3} value={form.remarks} onChange={(e) => setForm((current) => ({ ...current, remarks: e.target.value }))} className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm outline-none dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-100" />
          </div>
        </div>
      </FormModal>
    </div>
  );
}
